import { useCallback, useRef, useState, type ReactNode } from 'react';
import { t } from '../i18n';

/**
 * The mockup's tooltip: a 15px `i` button with the explanation hidden behind it.
 *
 * A working system does not keep paragraphs explaining itself on screen — the mockup makes
 * that point by running `polish()` over every `<p class="note">` at build time and turning
 * each one into one of these. The console does it at the call site instead: an explanation
 * that an operator genuinely needs is a `Tip`, and everything that was only ever addressed
 * to a reviewer is gone rather than hidden.
 *
 * Opens on hover and on focus, so it is reachable by keyboard. `aria-describedby` ties the
 * body to the button, which is what makes a screen reader read it as the button's
 * description rather than as a stray paragraph.
 */
let tipSeq = 0;

/** The widest the panel is allowed to get — `.tipbody { max-width: min(420px, 78vw) }`. */
const TIP_MAX = 420;

export function Tip({ children, label, align }: { children: ReactNode; label?: string; align?: 'right' }) {
  const idRef = useRef<string | null>(null);
  if (!idRef.current) idRef.current = `tip-${++tipSeq}`;
  const btn = useRef<HTMLButtonElement>(null);
  const [flipped, setFlipped] = useState(align === 'right');

  /**
   * The mockup does this in `placeTip()` for the same reason: the panel opens to the right
   * of the button, and a Tip in the 320px side column would push the document wider than
   * the viewport — a horizontal scrollbar that appears on hover. Measured when it opens,
   * because the width it needs is not known until then.
   */
  const place = useCallback(() => {
    if (align === 'right') return;
    const r = btn.current?.getBoundingClientRect();
    if (!r) return;
    setFlipped(r.left + Math.min(TIP_MAX, window.innerWidth * 0.78) > window.innerWidth - 12);
  }, [align]);

  return (
    <span className={`tip${flipped ? ' tip-right' : ''}`} onMouseEnter={place} onFocus={place}>
      <button
        ref={btn}
        type="button"
        className="tipbtn"
        aria-label={label ?? t('tip.default.label')}
        aria-describedby={idRef.current}
      >
        i
      </button>
      <span className="tipbody" role="tooltip" id={idRef.current}>
        {children}
      </span>
    </span>
  );
}

/**
 * The flat KKP page head from the mockup: a purple title with an optional status chip, and
 * the screen's one-line description behind an `i` button — exactly where the mockup puts it
 * (`head()` in build.mjs wraps the lead in `tip()`), never as a paragraph under the title.
 */
export function PageHead({ title, lead, chips }: { title: string; lead?: string; chips?: ReactNode }) {
  return (
    <div className="page-head">
      <h1>
        {title}
        {chips}
        {lead && <Tip label={t('tip.screen.label')}>{lead}</Tip>}
      </h1>
    </div>
  );
}

export interface SubTab {
  id: string;
  label: string;
  /** Optional count badge, as the old Review tab carried. */
  badge?: ReactNode;
}

/**
 * The in-screen tab strip. `role="tab"` with the plain label as its accessible name, which
 * is also how the existing suite navigates. Drawn as the mockup's segmented control: one
 * flat row of square buttons, the selected one filled solid purple.
 */
export function SubTabs({
  tabs,
  active,
  onSelect,
  label,
}: {
  tabs: readonly SubTab[];
  active: string;
  onSelect: (id: string) => void;
  label: string;
}) {
  return (
    <nav className="tabs" role="tablist" aria-label={label}>
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          className="tab"
          aria-selected={active === tab.id}
          onClick={() => onSelect(tab.id)}
        >
          {tab.label}
          {tab.badge !== undefined && tab.badge !== null && <span className="count">{tab.badge}</span>}
        </button>
      ))}
    </nav>
  );
}
